import { FeatureId } from '@upright/common/iam/access/feature-access/types';
import { InternalToolsBar } from '@upright/ui/common/upright/internal/internal-tools-bar';
import { useEffect, useRef, useState } from 'react';
import { useHotkeys } from 'react-hotkeys-hook';
import styled from 'styled-components';
import { getTextStyle } from '../../content/texts/bou-text/text-style';
import { BouColor } from '../../global/bou-colors';
import { useHasAccessToFeature, useIsTeam, useViewSetting } from '../../hooks';
import { isScreenshotMode } from '../../utilities/debug-mode';
import { internalToolsBarKey } from '../../utilities/storage';
import { BlanketItem } from './common-items/blanket-item';
import { InternalToolsItem } from './common-items/internal-tools-item';
import { RemoveScroll } from 'react-remove-scroll';

export interface ItemsToShow {
  internalTools?: boolean;
  blanket?: boolean;
  modelVersion?: boolean;
}

interface Props {
  standardItemsToShow?: ItemsToShow;
}

export function BouStatusBar(props: Props) {
  const internalToolsVisibleLocalStorage = localStorage.getItem(internalToolsBarKey);

  const [internalToolsVisible, setInternalToolsVisible] = useState(internalToolsVisibleLocalStorage === 'true');

  function toggleInternalTools() {
    setInternalToolsVisible(!internalToolsVisible);
    if (internalToolsVisible) {
      localStorage.setItem(internalToolsBarKey, 'false');
    } else {
      localStorage.setItem(internalToolsBarKey, 'true');
    }
  }

  useHotkeys('alt+p', () => {
    toggleInternalTools();
  });

  const isTeam = useIsTeam();
  const canAccessBlanket = useHasAccessToFeature(FeatureId.BLANKET_LIMITED);
  const visible = useViewSetting('internalAdmin.showStatusBar');

  const renderInternalTools = isTeam && !isScreenshotMode() && internalToolsVisible && visible;

  const internalToolsBarRef = useRef<HTMLDivElement | null>(null);

  // Expose the internal-tools-bar visibility as a `<body>` class so visually
  // adjacent surfaces rendered outside this component tree can react to it
  // without lifting state up through context / Redux. Currently consumed by
  // the floating chat side panel (`.chat-side-panel` in
  // `src/ui/chat/upright-chat-theme.css`), which uses the absence of this
  // class to widen its bottom margin so the panel reads as a balanced
  // floating card when the extra bottom chrome isn't present (PLT-4675). If
  // more consumers ever appear, prefer that they read the same class rather
  // than each duplicating the localStorage probe.
  //
  // Additionally, when the bar is open we publish its measured height as the
  // `--bou-internal-tools-bar-height` CSS variable on `<body>`. The bar
  // renders *above* the fixed 25px status bar (overflowing it upward), so the
  // shared page frame's `BottomSpacer` reserves `25px + this variable` of
  // scroll space — otherwise the bottom of the page content is hidden behind
  // the open bar and cannot be scrolled into view (PLT-5040). The height is
  // measured rather than hardcoded because the bar is content-driven and wraps
  // on narrow viewports. The variable defaults to `0px` when the bar is
  // closed, so closed-bar layout is unchanged.
  useEffect(() => {
    document.body.classList.toggle('has-internal-tools-bar', renderInternalTools);

    if (!renderInternalTools) {
      document.body.style.removeProperty('--bou-internal-tools-bar-height');
      return () => {
        document.body.classList.remove('has-internal-tools-bar');
      };
    }

    const updateHeight = () => {
      const height = internalToolsBarRef.current?.offsetHeight ?? 0;
      document.body.style.setProperty('--bou-internal-tools-bar-height', `${height}px`);
    };
    updateHeight();

    const resizeObserver = new ResizeObserver(updateHeight);
    if (internalToolsBarRef.current) {
      resizeObserver.observe(internalToolsBarRef.current);
    }

    return () => {
      resizeObserver.disconnect();
      document.body.classList.remove('has-internal-tools-bar');
      document.body.style.removeProperty('--bou-internal-tools-bar-height');
    };
  }, [renderInternalTools]);

  const items = props.standardItemsToShow || {
    internalTools: true,
    // Blanket is shown here for non-team users and in internal tools for team users
    blanket: canAccessBlanket && !isTeam,
    modelVersion: true,
  };

  if (!isTeam || !visible) {
    return null;
  }

  return (
    <Root className={RemoveScroll.classNames.fullWidth}>
      {/* HeaderReleaseSelector uses Radix which uses a lib called react-remove-scroll under the hood.
          The lib removes the scrollbar when the Radix Select component is active, which causes fixed elements to jump around.
          We use a helper class provided by the lib to prevent this. See https://github.com/radix-ui/primitives/discussions/1586
       */}
      {renderInternalTools && (
        <NavigationWrap ref={internalToolsBarRef}>
          <InternalToolsBar />
        </NavigationWrap>
      )}
      <BottomBar>
        <Left />
        <Right>
          {items.internalTools && (
            <InternalToolsItem
              toolsVisible={internalToolsVisible}
              onClick={() => {
                toggleInternalTools();
              }}
            />
          )}
          {items.blanket && <BlanketItem />}
        </Right>
      </BottomBar>
    </Root>
  );
}

const Root = styled.div`
  position: absolute;
  right: -15px;
  left: 0;
  top: 0;
  bottom: 0;
  z-index: 10;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
`;

const NavigationWrap = styled.div`
  width: 100%;
`;

const BottomBar = styled.div`
  background-color: ${BouColor.TOOL_FRAME_LIGHT_GRAY};
  ${getTextStyle({ size: 'xxxxs' })};
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-shrink: 0;
  height: 100%;
  padding-right: 15px;
`;

const Left = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
`;

const Right = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
`;
